// Module-level state of the daily routes screen, per vhost. It outlives the
// screen: the draft survives a tab switch, and a save in flight keeps running
// (and lands in the latest draft) even when the screen is unmounted.
import type { Client } from "@xmpp/client"
import {
  afterSave,
  changesForRoute,
  discardRoute,
  isRouteDirty,
  type DailyRoutesDraft,
} from "./draft"
import { ConfigTooLargeError, saveRoute } from "./storage"
import i18n from "@/lib/i18n/i18n"

export type DailyRoutesSession = {
  draft: DailyRoutesDraft | null
  // the route being saved right now; only one save runs at a time
  saving: string | null
  errors: Record<string, string>
}

const EMPTY: DailyRoutesSession = { draft: null, saving: null, errors: {} }

const sessions = new Map<string, DailyRoutesSession>()
const listeners = new Set<() => void>()

export const getSession = (vhost: string): DailyRoutesSession =>
  sessions.get(vhost) ?? EMPTY

export const subscribeSessions = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const update = (
  vhost: string,
  change: (current: DailyRoutesSession) => Partial<DailyRoutesSession>,
) => {
  const current = getSession(vhost)
  sessions.set(vhost, { ...current, ...change(current) })
  listeners.forEach((listener) => listener())
}

const withoutError = (errors: Record<string, string>, route: string) =>
  Object.fromEntries(Object.entries(errors).filter(([r]) => r !== route))

export const hasDirtyRoute = (draft: DailyRoutesDraft | null): boolean =>
  draft !== null &&
  Object.keys(draft.orders).some((r) => isRouteDirty(draft, r))

export const resetDailyRoutesCache = () => {
  sessions.clear()
  listeners.forEach((listener) => listener())
}

export const setLoadedDraft = (vhost: string, draft: DailyRoutesDraft) =>
  update(vhost, () => ({ draft, errors: {} }))

export const editDraft = (vhost: string, draft: DailyRoutesDraft) =>
  update(vhost, () => ({ draft }))

export const discardDraftRoute = (vhost: string, route: string) =>
  update(vhost, ({ draft, errors }) => ({
    draft: draft && discardRoute(draft, route),
    errors: withoutError(errors, route),
  }))

export const startSave = async (
  client: Client,
  vhost: string,
  route: string,
): Promise<void> => {
  const { draft, saving } = getSession(vhost)
  if (saving !== null || draft === null) return
  update(vhost, () => ({ saving: route }))
  try {
    const written = await saveRoute(client, changesForRoute(draft, route))
    update(vhost, ({ draft: latest, errors }) => ({
      // the draft as it is now, not as it was when the save started
      draft: latest && afterSave(latest, route, written),
      errors: withoutError(errors, route),
      saving: null,
    }))
  } catch (error: unknown) {
    update(vhost, ({ errors }) => ({
      errors: {
        ...errors,
        [route]:
          error instanceof ConfigTooLargeError
            ? i18n.t("maps.dailyRoutesSession.tooLarge")
            : i18n.t("maps.dailyRoutesSession.saveFailed", { route }),
      },
      saving: null,
    }))
  } finally {
    // whatever went wrong above, the vhost must not stay locked
    if (getSession(vhost).saving !== null)
      update(vhost, () => ({ saving: null }))
  }
}
